import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star, MessageCircle, ChevronLeft, ChevronRight, MapPin, Quote } from "lucide-react";
import { Reveal, MaskedLines } from "../components/motion";
import { EnquiryDialog } from "../components/EnquiryDialog";
import { PackageCard } from "../components/PackageCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { HERO_VIDEO, HERO_SLIDES, FEATURES, PACKAGES, DESTINATIONS, TESTIMONIALS, PROCESS, FAQS, GALLERY, CONTACT } from "../data/site";

function Hero() {
  const [idx, setIdx] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative h-[100svh] min-h-[600px] overflow-hidden bg-emerald-dark" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <video
          className="w-full h-[120%] object-cover"
          autoPlay muted loop playsInline
          poster={HERO_SLIDES[0].img}
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-emerald-dark/70" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col justify-end pb-24 pt-28">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow text-gold-light mb-5">
          Mumbai → Kerala · Premium Tour Specialists
        </motion.p>
        <h1 className="font-serif text-cream font-light leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-5xl tracking-tight">
          <MaskedLines lines={["Experience God's Own", "Country Like Never Before"]} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="text-cream/80 text-lg max-w-xl mt-8 leading-relaxed">
          Premium Mumbai to Kerala tour packages with comfortable hotels, trusted drivers and personalised service.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
          className="flex flex-wrap gap-4 mt-10">
          <EnquiryDialog title="Book Your Tour" presetType="Booking" trigger={
            <button className="bg-cream text-emerald px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-gold hover:text-white transition-colors" data-testid="hero-book-btn">Book Your Tour</button>
          } />
          <EnquiryDialog title="Get a Free Quote" trigger={
            <button className="border border-cream/50 text-cream px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-cream hover:text-emerald transition-colors" data-testid="hero-quote-btn">Get Free Quote</button>
          } />
          <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
            className="flex items-center gap-2 bg-emerald text-cream px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald-light transition-colors">
            <MessageCircle className="w-4 h-4" /> WhatsApp Now
          </a>
        </motion.div>

        <div className="flex items-center gap-4 mt-14">
          {HERO_SLIDES.map((s, i) => (
            <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`}
              className={`text-left transition-opacity ${i === idx ? "opacity-100" : "opacity-40 hover:opacity-70"}`}>
              <div className={`h-[2px] w-12 mb-2 ${i === idx ? "bg-gold-light" : "bg-cream/50"}`} />
              <span className="text-cream text-xs uppercase tracking-widest">{s.place}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = ["Houseboat Stays", "Honeymoon Escapes", "Family Tours", "Tea Garden Trails", "Wildlife Safaris", "Group Journeys", "Airport Transfers", "Custom Itineraries"];
  return (
    <div className="bg-emerald text-cream py-6 overflow-hidden" data-testid="marquee">
      <div className="marquee-track">
        {[...items, ...items].map((it, i) => (
          <span key={i} className="font-serif text-2xl lg:text-3xl font-light px-12 flex items-center gap-12 whitespace-nowrap">
            {it} <span className="text-gold-light">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function WhyChoose() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32" data-testid="why-choose">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow text-gold mb-5">Why Kerala Holidays Connect</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-emerald leading-tight">A travel partner Mumbai families genuinely trust.</h2>
            <p className="text-ink/60 mt-6 leading-relaxed">We do one thing, exceptionally well — crafting seamless Kerala holidays for travellers from Mumbai. No generic packages, only journeys built around you.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-12">
          {FEATURES.map((f, i) => (
            <Reveal key={f.n} delay={i * 0.08}>
              <div className="border-t border-emerald/15 pt-6">
                <span className="font-serif text-5xl text-gold/40 font-light">{f.n}</span>
                <h3 className="font-serif text-2xl text-emerald mt-3">{f.title}</h3>
                <p className="text-ink/60 mt-3 leading-relaxed text-sm">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="packages-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <Reveal>
            <p className="eyebrow text-gold mb-4">Popular Kerala Tour Packages</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light text-emerald leading-none max-w-2xl">Curated journeys, ready when you are.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/packages" className="flex items-center gap-2 text-emerald hover-underline text-sm font-medium" data-testid="view-all-packages">View all packages <ArrowUpRight className="w-4 h-4" /></Link>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {PACKAGES.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.1}><PackageCard pkg={p} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Destinations() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32" data-testid="destinations-section">
      <Reveal>
        <p className="eyebrow text-gold mb-4">Kerala Destinations</p>
        <h2 className="font-serif text-4xl lg:text-6xl font-light text-emerald leading-none mb-16 max-w-3xl">Six landscapes, one unforgettable state.</h2>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-6">
        {DESTINATIONS.map((d, i) => (
          <Reveal key={d.name} delay={(i % 3) * 0.08}>
            <div className={`group relative overflow-hidden ${i % 2 === 0 ? "h-[420px]" : "h-[340px] md:mt-16"}`} data-testid={`destination-${d.name.toLowerCase()}`}>
              <img src={d.img} alt={d.name} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-dark/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 p-6 text-cream">
                <span className="eyebrow text-gold-light">{d.tag}</span>
                <h3 className="font-serif text-3xl font-light mt-1 flex items-center gap-2"><MapPin className="w-5 h-5 text-gold-light" />{d.name}</h3>
                <p className="text-cream/70 text-sm mt-2 max-w-xs">{d.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Process() {
  return (
    <section className="relative bg-emerald-dark text-cream grain py-24 lg:py-32" data-testid="process-section">
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10">
        <Reveal><p className="eyebrow text-gold-light mb-4">Tour Booking Process</p>
          <h2 className="font-serif text-4xl lg:text-6xl font-light leading-none mb-16 max-w-2xl">Four simple steps to your Kerala escape.</h2></Reveal>
        <div className="grid md:grid-cols-4 gap-10">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.1}>
              <div className="border-t border-cream/20 pt-6">
                <span className="font-serif text-6xl text-gold-light/50 font-light">{p.n}</span>
                <h3 className="font-serif text-2xl mt-3">{p.title}</h3>
                <p className="text-cream/60 mt-3 text-sm leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="testimonials-section">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <Reveal><p className="eyebrow text-gold mb-4">Customer Testimonials · Google Reviews</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light text-emerald leading-none max-w-2xl">Loved by travellers from Mumbai.</h2></Reveal>
          <Reveal delay={0.1}><div className="flex items-center gap-3">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" />)}</div>
            <span className="text-ink/70 text-sm">4.9 / 5 · 500+ happy journeys</span>
          </div></Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div className="border border-emerald/10 p-8 h-full hover:-translate-y-1 hover:shadow-xl hover:shadow-black/5 transition-all duration-300 bg-cream" data-testid={`testimonial-${i}`}>
                <Quote className="w-8 h-8 text-gold/40" />
                <div className="flex my-4">{[...Array(t.rating)].map((_, s) => <Star key={s} className="w-4 h-4 fill-gold text-gold" />)}</div>
                <p className="text-ink/80 leading-relaxed font-serif text-lg italic">“{t.text}”</p>
                <div className="mt-6 pt-4 border-t border-emerald/10">
                  <p className="font-semibold text-emerald">{t.name}</p>
                  <p className="text-ink/50 text-xs mt-0.5">{t.from} · {t.type}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GallerySection() {
  return (
    <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32" data-testid="gallery-section">
      <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
        <Reveal><p className="eyebrow text-gold mb-4">Gallery</p>
          <h2 className="font-serif text-4xl lg:text-6xl font-light text-emerald leading-none">Moments from the road.</h2></Reveal>
        <Reveal delay={0.1}><Link to="/gallery" className="flex items-center gap-2 text-emerald hover-underline text-sm" data-testid="view-gallery">Full gallery <ArrowUpRight className="w-4 h-4" /></Link></Reveal>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {GALLERY.slice(0, 8).map((src, i) => (
          <Reveal key={i} delay={(i % 4) * 0.06}>
            <div className={`overflow-hidden group ${i % 5 === 0 ? "row-span-2 h-full min-h-[240px]" : "h-[200px]"}`}>
              <img src={src} alt="Kerala" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="faq-section">
      <div className="max-w-[1000px] mx-auto px-5 lg:px-10">
        <Reveal><p className="eyebrow text-gold mb-4 text-center">Frequently Asked Questions</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-light text-emerald leading-none mb-14 text-center">Everything you need to know.</h2></Reveal>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-emerald/10" data-testid={`faq-${i}`}>
              <AccordionTrigger className="font-serif text-xl lg:text-2xl text-emerald text-left hover:no-underline py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-ink/60 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function ContactMap() {
  return (
    <section className="relative bg-emerald-dark text-cream grain py-24 lg:py-32" data-testid="contact-section" id="contact">
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <Reveal>
            <p className="eyebrow text-gold-light mb-4">Start Planning</p>
            <h2 className="font-serif text-4xl lg:text-6xl font-light leading-none">Your Kerala journey<br />begins with hello.</h2>
            <p className="text-cream/70 mt-6 max-w-md leading-relaxed">Tell us your dates and dreams. A dedicated travel expert will craft a free, no-obligation itinerary just for you.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <EnquiryDialog title="Get a Free Quote" trigger={<button className="bg-gold text-white px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-cream hover:text-emerald transition-colors" data-testid="contact-quote-btn">Get Free Quote</button>} />
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-cream/40 px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-cream hover:text-emerald transition-colors" data-testid="contact-whatsapp-btn"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="overflow-hidden border border-cream/10 h-[360px]">
            <iframe title="Kerala Holidays Connect Office" src={CONTACT.mapEmbed} width="100%" height="100%" style={{ border: 0, filter: "grayscale(0.3)" }} loading="lazy" data-testid="google-map" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <WhyChoose />
      <Packages />
      <Destinations />
      <Process />
      <Testimonials />
      <GallerySection />
      <Faq />
      <ContactMap />
    </div>
  );
}
